/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import isEmpty from 'lodash/isEmpty';

import {
  PagingState,
  SortingState,
  CustomPaging,
  FilteringState,
  SearchState,
  IntegratedFiltering,
  IntegratedSorting,
  IntegratedPaging,
  // IntegratedGrouping,
  // SelectionState,
  // IntegratedSelection,
  RowDetailState,
  SummaryState,
  CustomSummary,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  Table,
  VirtualTable,
  TableHeaderRow,
  TableColumnVisibility,
  TableColumnResizing,
  // TableSelection,
  TableFilterRow,
  Toolbar,
  ColumnChooser,
  PagingPanel,
  TableRowDetail,
  TableSummaryRow,
  TableFixedColumns,
} from '@devexpress/dx-react-grid-bootstrap4';

import '@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css';

import { Button } from '../buttons';
import Icon from '../Icon';

import DataTableLoader from './DataTableLoader';
import DataTablePagingPanel from './DataTablePagingPanel';
import DataTableRow from './DataTableRow';
import DataTableToolbar from './DataTableToolbar';
import HeaderFilter from './HeaderFilter';
import type { HeaderFilterOption } from './HeaderFilter';

const GridRoot = (props: any) => <Grid.Root { ...props } style={ { boxShadow: 'none' } } />;

const tableSummaryMessages = {
  count: 'Кол-во',
  sum: 'Итого',
};

// Переключатель раскрытия детальной строки. Штатная иконка DX — шрифт Open Iconic
// (oi-chevron-*), который в проект не подключён (глиф не отображается), поэтому
// рисуем core Icon (inline SVG). Повторяет TableDetailToggleCell/ExpandButton пакета.
const RowDetailToggleCell = ({
  expanded, onToggle, visible, tableColumn, tableRow, row, className, forwardedRef, ...restProps
}: any) => {
  const fireToggle = () => {
    if (visible === false) return;
    onToggle(!expanded);
  };

  return (
    <td
      { ...restProps }
      ref={ forwardedRef }
      className={ ['text-center align-middle', className].filter(Boolean).join(' ') }
    >
      <span
        role='button'
        tabIndex={ visible === false ? undefined : 0 }
        aria-expanded={ expanded }
        className='d-inline-flex p-2'
        onClick={ (e) => { e.stopPropagation();fireToggle(); } }
        onKeyDown={ (e) => {
          if (e.keyCode === 13 || e.keyCode === 32) {
            e.preventDefault();
            fireToggle();
          }
        } }
      >
        <Icon name={ expanded ? 'chevron-down' : 'chevron-right' } />
      </span>
    </td>
  );
};

// Стабильные ссылки для дефолтов-массивов (чтобы не создавать новые на каждом рендере)
const EMPTY_DATA = [];
const EMPTY_FILTERS = [];
const EMPTY_HIDDEN_COLUMN_NAMES = [];

// Ячейка шапки виртуальной таблицы — компактная, фиксированной высоты
const VirtualTableHeaderCell = (cellProps: any) => (
  <TableHeaderRow.Cell
    { ...cellProps }
    style={ { verticalAlign: 'middle', fontSize: '12px', height: '30px' } }
  />
);

// Ячейка шапки с мультифильтром: штатный контент DX (заголовок + контрол сортировки)
// и иконка-воронка рядом (поповер чекбоксов — см. HeaderFilter).
// Оба компонента обязаны жить на уровне модуля: cellComponent, созданный в теле
// рендера, меняет ссылку на каждом рендере, и React размонтирует ячейку целиком —
// поповер фильтра закрывался бы на каждом клике по чекбоксу.
const MultiFilterHeaderCell = (cellProps: any) => {
  if (!cellProps?.column?.options) {
    return <TableHeaderRow.Cell { ...cellProps } />;
  }

  return (
    <TableHeaderRow.Cell { ...cellProps }>
      <div className='d-flex align-items-center'>
        { cellProps.children }
        <HeaderFilter
          title={ cellProps.column.title }
          options={ cellProps.column.options }
          value={ cellProps.column.optionsValue }
          onChange={ cellProps.column.optionsFilter }
        />
      </div>
    </TableHeaderRow.Cell>
  );
};

/**
 * Описание колонки DataTable. Индексная сигнатура — колонки приходят из 100+
 * страниц с доменными полями (cellComponent, textAlign, showSum для CustomDataTable,
 * опции мультифильтра и т.п.); жёсткий тип здесь дал бы волну ложных ошибок.
 */
export interface DataTableColumn {
  /** Имя поля данных / идентификатор колонки. */
  name: string;
  title?: string;
  width?: number | string;
  minWidth?: number | string;
  align?: string;
  visible?: boolean;
  togglingEnabled?: boolean;
  wordWrapEnabled?: boolean;
  /** Закрепление слева (TableFixedColumns). */
  fixed?: boolean | string;
  getCellValue?: (row: any, columnName?: string) => any;
  /** Кастомный предикат фильтра (IntegratedFiltering columnExtensions). */
  filterPredicate?: (value: any, filter: any, row: any) => boolean;
  /** Конвертер значения для сортировки. */
  sortValueConverter?: (value: any) => any;
  /** Мультифильтр шапки (showTableMultiFilterRow) — см. HeaderFilter. */
  options?: HeaderFilterOption[];
  optionsValue?: HeaderFilterOption[];
  optionsFilter?: (selected: HeaderFilterOption[]) => void;
  [key: string]: any;
}

/**
 * Таблица на DevExpress dx-react-grid: пагинация/сортировка/фильтры (серверные и
 * локальные), скелетон загрузки, ошибки, мультифильтр шапки, master-detail,
 * summaries, закреплённые и скрываемые колонки.
 */
export interface DataTableProps {
  loading?: boolean;
  data?: any[];
  dataLength?: number;
  columns: DataTableColumn[];
  /**
   * @default 'widget'
   * @see https://devexpress.github.io/devextreme-reactive/react/grid/docs/reference/table-column-resizing/
   */
  columnsResizingMode?: 'nextColumn' | 'widget';
  filters?: any[];
  searchValue?: string;
  currentPage?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: string;
  /** Отображает ошибку вместо таблицы. */
  error?: { message?: string } | null;
  errorRetryHandler?: (...args: unknown[]) => unknown;
  hideToolbar?: boolean;
  hidePagination?: boolean;
  localSorting?: boolean;
  localPaging?: boolean;
  dataRowComponent?: (props: any) => ReactNode;
  dataCellComponent?: (props: any) => ReactNode;
  toolbarComponent?: (props: any) => ReactNode;
  /**
   * CSS-класс строки: строка или функция от tableRow ({ rowId, row }).
   */
  rowClassName?: string | ((tableRow: any) => string | undefined | null);
  /**
   * Итоговая строка: массив итемов SummaryState или функция, возвращающая его.
   */
  rowSummary?: any[] | (() => any[]);
  defaultHiddenColumnNames?: string[];
  noDataText?: string;
  onSortingChange?: (columnName?: string, direction?: string) => void;
  onCurrentPageChange?: (offset: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  /**
   * Клик по строке; получает tableRow ({ rowId, row }).
   */
  onRowClick?: (tableRow: any) => void;
  onHideColumn?: (hiddenColumnNames: string[]) => void;
  height?: number;
  /** Показываем или нет таблицу с встроенным скроллингом. */
  showVirtualTable?: boolean;
  showTableFilterRow?: boolean;
  showTableMultiFilterRow?: boolean;
  isSettingsStored?: boolean;
  /** Контент раскрывающейся деталь-строки (master-detail); без него плагины не подключаются. */
  rowDetailComponent?: ComponentType<any>;
  /** Стабильный id строки (например, row => row.clientId) — для сохранения раскрытых деталь-строк. */
  getRowId?: (row: any) => number | string;
}

const DataTable = (props: DataTableProps) => {
  const {
    loading,
    columns,
    columnsResizingMode = 'widget',
    data = EMPTY_DATA,
    dataLength,
    error,
    errorRetryHandler,
    filters = EMPTY_FILTERS,
    searchValue = '',
    sortField,
    sortOrder,
    noDataText = 'По Вашему запросу ничего не найдено',
    defaultHiddenColumnNames = EMPTY_HIDDEN_COLUMN_NAMES,
    dataRowComponent,
    dataCellComponent,
    toolbarComponent,
    rowClassName,
    rowSummary,
    hideToolbar = false,
    hidePagination = false,
    localSorting,
    localPaging,
    onCurrentPageChange,
    onPageSizeChange,
    onSortingChange,
    onRowClick,
    onHideColumn,
    height,
    showVirtualTable = false,
    showTableFilterRow = false,
    showTableMultiFilterRow = false,
    isSettingsStored = false,
    // контент раскрывающейся деталь-строки (master-detail); без него плагины не подключаются
    rowDetailComponent,
    // стабильный id строки (например, row => row.clientId) — нужен для сохранения
    // раскрытых деталь-строк при сортировке/пагинации (по умолчанию — индекс строки)
    getRowId,
  } = props;

  const TableComponent = (internalProps: any) => (showVirtualTable
    ? (<VirtualTable.Table { ...internalProps } />)
    : (<Table.Table { ...internalProps } style={ { overflow: 'hidden' } } />));

  const [totalSummaryItems, setTotalSummaryItems] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(props.currentPage ?? 0);
  const [pageSize, setPageSize] = useState(props.pageSize ?? 10);
  const [sorting, setSorting] = useState<any[]>([{
    columnName: sortField,
    direction: sortOrder?.toLowerCase(),
  }]);

  const columnExtensions = [];
  const filteringExtensions = [];
  const sortingExtensions = [];
  // локальная копия — иначе мутируем переданный prop (а это может быть shared-массив defaultProps)
  const effectiveHiddenColumnNames = [...defaultHiddenColumnNames];

  columns.forEach(column => {
    columnExtensions.push({
      columnName: column.name,
      align: column.align,
      width: column.width || 200,
      togglingEnabled: column.togglingEnabled,
      wordWrapEnabled: column.wordWrapEnabled,
      minWidth: column.minWidth || 150,
    });

    if (typeof column.filterPredicate === 'function') {
      filteringExtensions.push({
        columnName: column.name,
        predicate: column.filterPredicate,
      });
    }

    if (typeof column.sortValueConverter === 'function') {
      sortingExtensions.push({
        columnName: column.name,
        compare: (a, b) => {
          const valueA = column.sortValueConverter(a);
          const valueB = column.sortValueConverter(b);

          if (valueA === valueB) {
            return 0;
          }

          return (valueA < valueB) ? -1 : 1;
        },
      });
    }

    if (column.visible === false && !isSettingsStored) {
      effectiveHiddenColumnNames.push(column.name);
    }
  });

  const fixedColumns = columns.filter(v => v.fixed).map(v => v.name);

  useEffect(() => {
    if (rowSummary) {
      setTotalSummaryItems((typeof rowSummary === 'function') ? rowSummary() : rowSummary);
    }
  }, [rowSummary]);

  const getTotalSummaryValues = () => {
    return totalSummaryItems.map((summary) => {
      return summary.value;
    });
  };

  const handleCurrentPageChange = (value: number) => {
    setCurrentPage(value);

    if (typeof onCurrentPageChange === 'function') {
      onCurrentPageChange(value * pageSize);
    }
  };

  const handlePageSizeChange = (value: number) => {
    // при изменении размера страницы сбросим пагинацию на первую страницу,
    // что бы избежать ситуации, когда currentPage превышает количество страниц
    // с учетом нового размера страницы
    setPageSize(value);
    setCurrentPage(0);

    if (typeof onPageSizeChange === 'function') {
      onPageSizeChange(value);
    }

    if (typeof onCurrentPageChange === 'function') {
      onCurrentPageChange(0);
    }
  };

  const handleSortingChange = (value: any[]) => {
    // такой вот костыль, чтоб запретить сортировку по нескольким полям (удерживая shift)
    // сортируем только по столбцу, по которому кликнули последним
    const lastSorting = value.slice(-1);

    setSorting(lastSorting);

    if (typeof onSortingChange === 'function') {
      const { columnName, direction } = lastSorting.length > 0 ? lastSorting[0] : ({} as any);

      onSortingChange(columnName, direction?.toUpperCase() || 'ASC');
    }
  };

  if (isEmpty(data) && error) {
    return (
      <div className='text-center text-danger'>
        Ошибка загрузки данных

        { error.message && (
          <div>{ error.message }</div>
        ) }
        <br />
        { errorRetryHandler && (
          <Button text='повторить' onClick={ errorRetryHandler } />
        ) }
      </div>
    );
  }

  let divStyle: Record<string, any> = { position: 'relative' };

  if (loading) {
    divStyle = {
      ...divStyle,
      opacity: '0.4',
      cursor: 'wait',
      minHeight: '350px',
    };
  }

  /* const virtualTableHeadComponent = ({ ...headProps }) => {
    return (
      <VirtualTable.TableHead
        { ...headProps }
        showSortingControls
        style={ {
          height: '30px',
        } }
      />
    );
  }; */

  return (loading && !data?.length) ? (
    <DataTableLoader rows={ pageSize } />
  ) : (
    <div style={ divStyle }>
      <Grid
        rows={ data }
        columns={ columns }
        rootComponent={ GridRoot }
        getRowId={ getRowId }
      >

        { rowDetailComponent && <RowDetailState /> }

        { showTableFilterRow
          ? (
            <FilteringState
              defaultFilters={ [] }
            />
          )
          : <FilteringState filters={ filters } /> }
        <SearchState value={ searchValue } />

        <IntegratedFiltering columnExtensions={ filteringExtensions } />
        { /* <SelectionState /> */ }
        { /* <IntegratedSelection /> */ }

        <SortingState sorting={ sorting } onSortingChange={ handleSortingChange } />
        { localSorting && <IntegratedSorting columnExtensions={ sortingExtensions } /> }

        { !!rowSummary && <SummaryState totalItems={ totalSummaryItems } /> }

        { !hidePagination && (
          <PagingState
            defaultCurrentPage={ 0 }
            defaultPageSize={ 10 }
            currentPage={ currentPage }
            pageSize={ pageSize }
            onCurrentPageChange={ handleCurrentPageChange }
            onPageSizeChange={ handlePageSizeChange }
          />
        ) }
        { /* totalCount на IntegratedPaging — легаси: рантайм его игнорирует (считает сам), сохранено 1:1 */ }
        { !hidePagination && localPaging && <IntegratedPaging { ...{ totalCount: dataLength } } /> }
        { !hidePagination && !localPaging && <CustomPaging totalCount={ dataLength } /> }

        { !!rowSummary && <CustomSummary totalValues={ getTotalSummaryValues() } /> }

        { showVirtualTable
        && (
          <VirtualTable
            height={ height ?? 760 }
            columnExtensions={ columnExtensions }
            tableComponent={ TableComponent }
            noDataRowComponent={ () => ((data.length === 0) ? (
              <tr><td className='text-center' colSpan={ '100%' as any }> { noDataText } </td></tr>
            ) : null) }
            // headComponent={ virtualTableHeadComponent }
            rowComponent={ rowProps => (dataRowComponent ? dataRowComponent(rowProps) : (
              <DataTableRow { ...rowProps } rowClassName={ rowClassName } onRowClick={ onRowClick } />
            )) }
            cellComponent={ cellProps => (dataCellComponent ? dataCellComponent(cellProps) : (
              <VirtualTable.Cell { ...cellProps } />
            )) }
          />
        ) }

        { !showVirtualTable
        && (
          <Table
            columnExtensions={ columnExtensions }
            tableComponent={ TableComponent }
            noDataRowComponent={ () => ((data.length === 0) ? (
              <tr><td className='text-center' colSpan={ '100%' as any }> { noDataText } </td></tr>
            ) : null) }
            rowComponent={ rowProps => (dataRowComponent ? dataRowComponent(rowProps) : (
              <DataTableRow { ...rowProps } rowClassName={ rowClassName } onRowClick={ onRowClick } />
            )) }
            cellComponent={ cellProps => (dataCellComponent ? dataCellComponent(cellProps) : (
              <Table.Cell { ...cellProps } />
            )) }
          />
        ) }

        { rowDetailComponent && (
          <TableRowDetail
            contentComponent={ rowDetailComponent }
            toggleCellComponent={ RowDetailToggleCell }
          />
        ) }

        <TableColumnResizing
          defaultColumnWidths={ columnExtensions }
          resizingMode={ columnsResizingMode }
          columnExtensions={ columnExtensions }
        />

        <TableHeaderRow
          showSortingControls
          // ссылки cell-компонентов стабильны между рендерами (см. комментарий у MultiFilterHeaderCell).
          // undefined передавать нельзя: themed-дефолты dx подмешиваются спредом {...defaults, ...props},
          // и явный undefined перетирает их — рендер падает с «Element type is invalid».
          // Штатная ячейка DX = TableHeaderRow.Cell.
          cellComponent={ showVirtualTable
            ? VirtualTableHeaderCell
            : (showTableMultiFilterRow ? MultiFilterHeaderCell : TableHeaderRow.Cell) }
        />
        { !hidePagination && (
          <PagingPanel
            pageSizes={ [5, 10, 20, 50, 100] }
            containerComponent={ (pagingProps) => (
              <DataTablePagingPanel { ...pagingProps } onChange={ handlePageSizeChange } />
            ) }
          />
        ) }

        { !hideToolbar && (
          <Toolbar
            rootComponent={ (toolbarProps) => (
              <DataTableToolbar
                dataLength={ dataLength }
                currentPage={ currentPage }
                pageSize={ pageSize }
                { ...toolbarProps }
              >
                { toolbarComponent ? toolbarComponent(toolbarProps) : toolbarProps.children }
              </DataTableToolbar>
            ) }
          />
        ) }

        { /* <TableSelection showSelectAll={true} /> */ }

        { showTableFilterRow && <TableFilterRow showFilterSelector={ true } /> }

        <TableColumnVisibility
          defaultHiddenColumnNames={ effectiveHiddenColumnNames }
          onHiddenColumnNamesChange={ onHideColumn }
          columnExtensions={ columnExtensions }
        />

        { /* Закрепляем первый столбец */ }
        <TableFixedColumns
          leftColumns={ fixedColumns }
        />

        { /* columns на ColumnChooser — легаси: рантайм берёт колонки из Grid-контекста, пропс игнорируется */ }
        { !hideToolbar && <ColumnChooser { ...({ columns } as any) } /> }

        { !!rowSummary && <TableSummaryRow messages={ tableSummaryMessages } /> }
      </Grid>
    </div>
  );
};

export default DataTable;
